import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { useForm } from 'react-hook-form';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { repositories } from 'api';
import { checkoutOperations } from 'store/ducks/checkout';
import { CartShopStats, ShopCartItem } from 'store/ducks/shopCart/types';
import { CheckoutValues } from 'store/ducks/checkout/types';
import { sumStatsInCartShopItems } from 'store/ducks/shopCart/selectors';
import Button from 'common/components/Button';
import { CART_TYPE, StoreBasicDataApiStructure } from 'common/types';
import { OrderSchema } from './common/validation';
import { routes } from '../../common/routes';
import { DEFAULT_GRID_CONTAINER_SPACING, CHANNEL } from 'common/constants';
import { shopCartOperations } from 'store/ducks/shopCart';
import FormPanel from './components/FormPanel';
import {
    FormPanelType,
    PAYMENT_METHOD,
    SHIPPING_FORM,
    PAYER_ADDRESS,
    BILLING_ADDRESS,
    SHIPPING_ADDRESS,
    REALIZATION_DATE,
    NOTES,
    CUSTOMER_EMAIL,
    DIFFERENT_DATA,
    PAYER_DATA,
    TAX_ID_NUMBER,
    SUBORDINATE_INTERNAL_ID,
    RECIPIENT_SCHOOL_DATA,
    RECIPIENT_ADDRESS,
    IS_GRV,
    IS_SUBORDINATE_UNIT,
    HAS_NO_NIP,
    HAS_NO_SUBORDINATE_INTERNAL_ID,
    COMPANY,
    STREET,
    STREET_NUMBER,
    POST_CODE,
    CITY, SCHOOL_EMAIL
} from "../../common/constants";
import { CheckoutType } from 'api/repositories';
import DifferentData from './components/DifferentData';
import CheckoutRepository from 'api/repositories/Checkout';
import { getCartToken } from '../../common/functions';
import KsefDialog from "../../../../../common/components/KsefDialog";

type Props = {
    carts: any;
    checkoutData: StoreBasicDataApiStructure<CheckoutType>;
    checkoutValues: CheckoutValues;
    fetchCheckoutData: Function;
    isPromoter: boolean;
    items: Array<ShopCartItem>;
    itemsStats: CartShopStats;
    removeItemFromShopCart: Function;
    setCheckoutValues: Function;
};

type MappingType = {
    me: StoreBasicDataApiStructure<repositories.MeType>;
    shopCart: Array<ShopCartItem>;
    simpleProduct: StoreBasicDataApiStructure<repositories.SimpleProductType>;
    checkout: {
        data: StoreBasicDataApiStructure<CheckoutType>;
        values: CheckoutValues;
    };
};

const Order: React.FC<Props> = ({
    carts,
    checkoutData,
    checkoutValues,
    fetchCheckoutData,
    isPromoter,
    items,
    setCheckoutValues,
}) => {
    const isOrderTypeSchool = checkoutValues.orderType === CART_TYPE.SCHOOL;
    const [differentData, setDifferentData] = useState(
        isOrderTypeSchool ? true : (checkoutValues.differentData ?? true));
    const { push } = useHistory();

    useEffect(() => {
        if (!checkoutValues.orderType) {
            push(routes.CART);
        }
        if (isOrderTypeSchool && !differentData) {
            setDifferentData(true);
        }
    }, [checkoutValues.orderType, isOrderTypeSchool, differentData, push]);
    
    const [payerKsefFlags, setPayerKsefFlags] = useState({
        payerIsSubordinateUnit: false,
        payerIsGrv: false,
        payerHasNoNip: false,
        payerHasNoSubordinateInternalId: false,
    });
    
    const [recipientSchoolKsefFlags, setRecipientSchoolKsefFlags] = useState({
        recipientSchoolHasNoNip: false,
        recipientSchoolHasNoSubordinateInternalId: false,
    });

    const { control, errors, handleSubmit, setError, setValue, triggerValidation } = useForm({
        validationSchema: OrderSchema(isPromoter, checkoutValues.orderType),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            [DIFFERENT_DATA] : isOrderTypeSchool ? true : (checkoutValues.differentData ?? true),
            [PAYER_DATA]: {
                [IS_SUBORDINATE_UNIT]: false,
                [IS_GRV]: false,
                [HAS_NO_NIP]: false,
                [HAS_NO_SUBORDINATE_INTERNAL_ID]: false,
            },
            [RECIPIENT_SCHOOL_DATA]: {
                [IS_SUBORDINATE_UNIT]: false,
                [IS_GRV]: false,
                [HAS_NO_NIP]: false,
                [HAS_NO_SUBORDINATE_INTERNAL_ID]: false,
                [SCHOOL_EMAIL]: '',
            },
        },
        validationContext: { ...payerKsefFlags, ...recipientSchoolKsefFlags },
    });

    const cartToken = getCartToken(carts, CHANNEL);

    const errorsCount = Object.keys(errors).length;
    const errorsExist = useMemo(() => errorsCount > 0, [errorsCount]);
    const productsCart = items.length;
    const [showMissingIdsDialog, setShowMissingIdsDialog] = useState(false);
    const shouldShowDialog = (): boolean => {
        const payerMissingBoth = payerKsefFlags.payerHasNoNip && payerKsefFlags.payerHasNoSubordinateInternalId;
        const recipientMissingBoth = recipientSchoolKsefFlags.recipientSchoolHasNoNip && recipientSchoolKsefFlags.recipientSchoolHasNoSubordinateInternalId;
        return isOrderTypeSchool && (payerMissingBoth || recipientMissingBoth);
    };
    
    const handleNextClick = () => {
        if (shouldShowDialog()) {
            setShowMissingIdsDialog(true);
            return;
        }
        handleSubmit(onSubmit)();
    };
    
    const submitToApi = useCallback((values: any) => {
        const api = new CheckoutRepository();
        const sanitizedValues = { ...values };
        
        if (sanitizedValues[PAYER_DATA]) {
            sanitizedValues[PAYER_DATA][HAS_NO_NIP] = payerKsefFlags.payerHasNoNip;
            sanitizedValues[PAYER_DATA][HAS_NO_SUBORDINATE_INTERNAL_ID] = payerKsefFlags.payerHasNoSubordinateInternalId;
        }
        if (sanitizedValues[RECIPIENT_SCHOOL_DATA]) {
            sanitizedValues[RECIPIENT_SCHOOL_DATA][HAS_NO_NIP] = recipientSchoolKsefFlags.recipientSchoolHasNoNip;
            sanitizedValues[RECIPIENT_SCHOOL_DATA][HAS_NO_SUBORDINATE_INTERNAL_ID] = recipientSchoolKsefFlags.recipientSchoolHasNoSubordinateInternalId;
        }
        
        setCheckoutValues({
            orderType: checkoutValues.orderType,
            ...sanitizedValues,
            ...(!isOrderTypeSchool && {
                [RECIPIENT_SCHOOL_DATA]: undefined,
                [PAYER_DATA]: undefined,
            }),
        });
        
        Promise.all([
            api.put({ data: { method: values[PAYMENT_METHOD] }, url: `checkout/${cartToken}/payment` }),
            api.put({ data: { method: values[SHIPPING_FORM] }, url: `checkout/${cartToken}/shipping` }),
            api.put({
                data: {
                    [BILLING_ADDRESS]: values[BILLING_ADDRESS],
                    ...(isOrderTypeSchool
                            ? { [PAYER_ADDRESS]: values[PAYER_DATA], [RECIPIENT_ADDRESS]: values[RECIPIENT_SCHOOL_DATA] }
                            : { [PAYER_ADDRESS]: values[PAYER_ADDRESS] || values[BILLING_ADDRESS] }
                    ),
                    [SHIPPING_ADDRESS]: values[SHIPPING_ADDRESS],
                },
                url: `checkout/${cartToken}/address`,
            }),
            api.put({ data: { code: values[REALIZATION_DATE] }, url: `grant/checkout/${cartToken}/realization-date` }),
            isPromoter
                ? api.put({ data: { email: values[CUSTOMER_EMAIL], notes: values[NOTES] }, url: `checkout/${cartToken}/promoter-data` })
                : null,
        ])
            .then(() => push(routes.SUMMARY))
            .catch(({ response }) => {
                const errors = response.data.errors;
                const keyMap: Record<string, string> = isOrderTypeSchool
                    ? {
                        'payerAddress.taxIdNumber': `${PAYER_DATA}.${TAX_ID_NUMBER}`,
                        'payerAddress.subordinateInternalId': `${PAYER_DATA}.${SUBORDINATE_INTERNAL_ID}`,
                        'payerAddress.company': `${PAYER_DATA}.${COMPANY}`,
                        'payerAddress.street': `${PAYER_DATA}.${STREET}`,
                        'payerAddress.streetNumber': `${PAYER_DATA}.${STREET_NUMBER}`,
                        'payerAddress.postcode': `${PAYER_DATA}.${POST_CODE}`,
                        'payerAddress.city': `${PAYER_DATA}.${CITY}`,
                        'recipientAddress.taxIdNumber': `${RECIPIENT_SCHOOL_DATA}.${TAX_ID_NUMBER}`,
                        'recipientAddress.subordinateInternalId': `${RECIPIENT_SCHOOL_DATA}.${SUBORDINATE_INTERNAL_ID}`,
                        'recipientAddress.company': `${RECIPIENT_SCHOOL_DATA}.${COMPANY}`,
                        'recipientAddress.street': `${RECIPIENT_SCHOOL_DATA}.${STREET}`,
                        'recipientAddress.streetNumber': `${RECIPIENT_SCHOOL_DATA}.${STREET_NUMBER}`,
                        'recipientAddress.postcode': `${RECIPIENT_SCHOOL_DATA}.${POST_CODE}`,
                        'recipientAddress.city': `${RECIPIENT_SCHOOL_DATA}.${CITY}`,
                        'recipientAddress.schoolEmail': `${RECIPIENT_SCHOOL_DATA}.${SCHOOL_EMAIL}`,
                    } : {};
                
                Object.keys(errors).forEach((key) => {
                    const mappedKey = keyMap[key] ?? key;
                    setError(mappedKey, '', errors[key]);
                });
            });
    }, [checkoutValues, push, setCheckoutValues, cartToken, isOrderTypeSchool, isPromoter, payerKsefFlags, recipientSchoolKsefFlags]);
    
    const onSubmit = useCallback((values: any) => {
        submitToApi(values);
    }, [submitToApi]);
    
    const handleDialogConfirm = () => {
        setShowMissingIdsDialog(false);
        handleSubmit(onSubmit)();
    };
    
    const handleDialogCancel = () => {
        setShowMissingIdsDialog(false);
    };

    const handlePrevClick = () => push(routes.CART);

    return productsCart ? (
        <>
            <form onSubmit={handleSubmit(onSubmit)} style={{ margin: '2rem 0' }}>
                <Grid container spacing={DEFAULT_GRID_CONTAINER_SPACING}>
                    <Grid item xs={12}>
                        <FormPanel
                            cartToken={cartToken}
                            checkoutData={checkoutData}
                            checkoutValues={checkoutValues}
                            control={control}
                            errors={errors}
                            fetchCheckoutData={fetchCheckoutData}
                            type={FormPanelType.payment}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormPanel
                            cartToken={cartToken}
                            checkoutData={checkoutData}
                            checkoutValues={checkoutValues}
                            control={control}
                            errors={errors}
                            fetchCheckoutData={fetchCheckoutData}
                            type={FormPanelType.realizationDate}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormPanel
                            cartToken={cartToken}
                            checkoutData={checkoutData}
                            checkoutValues={checkoutValues}
                            control={control}
                            errors={errors}
                            fetchCheckoutData={fetchCheckoutData}
                            type={FormPanelType.shipmentForm}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormPanel
                            checkoutValues={checkoutValues}
                            control={control}
                            differentData={differentData}
                            errors={errors}
                            type={FormPanelType.invoiceData}
                            title={isOrderTypeSchool ? 'Dane do faktury<br/>(Podmiot 2)' : 'Dane do faktury'}
                        />
                    </Grid>
                    <Grid item xs={12} style={{ display: isOrderTypeSchool ? 'none' : 'block'}}>
                        <DifferentData
                            control={control}
                            differentData={differentData}
                            setCheckoutValues={setCheckoutValues}
                            setDifferentData={setDifferentData}
                        />
                    </Grid>
                    {differentData && !isOrderTypeSchool? (
                        <Grid item xs={12}>
                            <FormPanel
                                checkoutValues={checkoutValues}
                                control={control}
                                errors={errors}
                                type={FormPanelType.recipientData}
                            />
                        </Grid>
                    ) : null}
                    {isOrderTypeSchool ? (
                        <>
                            <Grid item xs={12}>
                                <FormPanel
                                    checkoutValues={checkoutValues}
                                    control={control}
                                    errors={errors}
                                    setValue={setValue}
                                    type={FormPanelType.payerData}
                                    onKsefFlagsChange={(payerIsSubordinateUnit: boolean, payerIsGrv: boolean, payerHasNoNip: boolean, payerHasNoSubordinateInternalId: boolean) => {
                                        setPayerKsefFlags({ payerIsSubordinateUnit, payerIsGrv, payerHasNoNip, payerHasNoSubordinateInternalId });
                                        setTimeout(() => {
                                            triggerValidation([`${PAYER_DATA}.${TAX_ID_NUMBER}`, `${PAYER_DATA}.${SUBORDINATE_INTERNAL_ID}`]);
                                        }, 0);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormPanel
                                    checkoutValues={checkoutValues}
                                    control={control}
                                    errors={errors}
                                    setValue={setValue}
                                    type={FormPanelType.recipientSchoolData}
                                    payerKsefFlags={payerKsefFlags}
                                    onKsefFlagsChange={(recipientSchoolHasNoNip: boolean, recipientSchoolHasNoSubordinateInternalId: boolean) => {
                                        setRecipientSchoolKsefFlags({ recipientSchoolHasNoNip, recipientSchoolHasNoSubordinateInternalId });
                                        setTimeout(() => {
                                            triggerValidation([`${RECIPIENT_SCHOOL_DATA}.${TAX_ID_NUMBER}`, `${RECIPIENT_SCHOOL_DATA}.${SUBORDINATE_INTERNAL_ID}`]);
                                        }, 0);
                                    }}
                                />
                            </Grid>
                        </>
                    ) : null}
                    <Grid item xs={12}>
                        <FormPanel
                            checkoutValues={checkoutValues}
                            control={control}
                            errors={errors}
                            orderType={checkoutValues.orderType}
                            type={FormPanelType.shipmentData}
                        />
                    </Grid>
                    {isPromoter ? (
                        <>
                            <Grid item xs={12}>
                                <FormPanel
                                    checkoutValues={checkoutValues}
                                    control={control}
                                    errors={errors}
                                    type={FormPanelType.customerEmail}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormPanel
                                    checkoutValues={checkoutValues}
                                    control={control}
                                    errors={errors}
                                    type={FormPanelType.notes}
                                />
                            </Grid>
                        </>
                    ) : null}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between">
                            <Button
                                colorTheme="darkgray"
                                onClick={handlePrevClick}
                                size="large"
                                type="button"
                            >
                                Wstecz
                            </Button>
                            <Button disabled={errorsExist} size="large" type="button" onClick={handleNextClick}>
                                Dalej
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
            <KsefDialog
                open={showMissingIdsDialog}
                onConfirm={handleDialogConfirm}
                onCancel={handleDialogCancel}
            />
        </>
    ) : null;
};

const mapStateToProps = (state: MappingType) => ({
    ...state.simpleProduct,
    carts: state.me.data?.carts,
    isPromoter: state.me.data?.isPromoter,
    items: state.shopCart,
    itemsStats: sumStatsInCartShopItems(state),
    checkoutData: state.checkout.data,
    checkoutValues: state.checkout.values,
});

const mapDispatchToProps = {
    changeProductQuantity: shopCartOperations.changeProductQuantity,
    removeItemFromShopCart: shopCartOperations.removeItemFromShopCart,
    fetchCheckoutData: (url: string) => checkoutOperations.fetchCheckoutData.request(url),
    setCheckoutValues: checkoutOperations.setCheckoutValues,
};

export default connect(mapStateToProps, mapDispatchToProps)(Order);
